/* eslint-disable complexity */
import {
    InformationCircleIcon,
    RefreshIcon,
    XIcon,
} from '../../Icon'
import React, {useContext} from 'react'
import {
    messageContainer,
    messageTime,
    nameText,
    speechBubble,
    speechBubbleContainer,
} from '../ChannelMessage/styles/ChannelMessage'
import {ChannelMessageProps} from '../types'
import {EmbedContext} from 'components/EmbedContext'
import File from './File'
import Highlight from '../../Highlight'
import {MessageStatus} from '../../../api/types/properties'
import {NinchatProperties} from '../../../api/types'
import PullRequestView from './GitHub/PullRequestView'
import PushView from './GitHub/PushView'
import UserAvatar from '../../Avatar/UserAvatar'
import Writing from './Writing'
import {styled} from '../../../stitches/config'
import {styledGithubMessage} from './GitHub/styles'
import {useTranslation} from 'react-i18next'
import {useUiState} from '../../../hooks'

const StyledMessageContainer = styled('div', messageContainer)
const StyledSpeechBubbleContainer = styled('div', speechBubbleContainer)
const StyledName = styled('span', nameText)
const StyledSpeechBubble = styled('div', speechBubble)
const StyledTime = styled('time', messageTime)
const StyledFilesContainer = styled('div', {
    display: 'flex',
    flexWrap: 'wrap',
    marginTop: '4px',
})
const StyledGithubMessage = styled('div', styledGithubMessage)
const StyledStatus = styled('div', {
    '& svg': {
        height: '14px',
        width: '14px',
    },
    '@media (max-width: 360px)': {justifyContent: 'flex-start'},
    'alignItems': 'center',
    'display': 'flex',
    'flexBasis': '100%',
    'flexWrap': 'wrap',
    'fontSize': '12px',
    'gap': '6px',
    'justifyContent': 'flex-end',
    'lineHeight': 1,
    'marginTop': '4px',
    'variants': {
        failed: {true: {color: '$error'}},
        pending: {true: {color: '$messageInfoIcon'}},
    },
})
const StyledStatusText = styled('span', {'@media (max-width: 360px)': {flexBasis: '100%'}})
const StyledStatusActions = styled('span', {
    '@media (max-width: 360px)': {
        flexBasis: '100%',
        justifyContent: 'flex-end',
    },
    'alignItems': 'center',
    'display': 'inline-flex',
    'gap': '6px',
})
const StyledStatusButton = styled('button', {
    '& svg': {
        height: '14px',
        width: '14px',
    },
    '&:hover': {textDecoration: 'underline'},
    'alignItems': 'center',
    'background': 'none',
    'border': 0,
    'color': '$messageSpeechBubbleOtherText',
    'cursor': 'pointer',
    'display': 'inline-flex',
    'flexShrink': 0,
    'font': 'inherit',
    'gap': '3px',
    'padding': 0,
    'whiteSpace': 'nowrap',
})

const ChannelMessage = (props: ChannelMessageProps): React.ReactElement => {
    const {
        isMe,
        isRepeated,
        isWriting,
        message,
        iconUrl,
        time,
        rights,
        onRemovePendingMessage,
        onRetryMessage,
    } = props

    const ui = useUiState()
    const {t} = useTranslation()
    const embed = useContext(EmbedContext)
    const isEmbedQueueChannel = embed?.variant === 'queue' || embed?.variant === 'channel'

    // eslint-disable-next-line no-extra-parens
    const showAvatar = !isEmbedQueueChannel || (isEmbedQueueChannel && (embed?.messageAvatars === 'all' || (embed?.messageAvatars === 'agentsOnly' && !isMe)))
    const isAgentMessage = isEmbedQueueChannel && !isMe
    const name = isAgentMessage && embed?.agentName ? embed.agentName : message.message_user_name
    const avatarIconOverride = isAgentMessage ? embed?.agentAvatar : undefined

    const getHref = (json: NinchatProperties.JSON): string => `data:application/json,${escape(JSON.stringify(json).replaceAll(' ', '\\u0020'))}`

    const getPreview = (json: NinchatProperties.JSON): string => {
        const jsonString = JSON.stringify(json, null, 1)
        return jsonString.length > 512 ? `${jsonString.substring(0, 512)}...` : jsonString
    }

    return (
        <StyledMessageContainer hidden={message.message_hidden} me={isMe} repeated={isRepeated}>
            {!isRepeated && showAvatar ?
                <UserAvatar
                    iconOverride={avatarIconOverride}
                    name={name}
                    size={ui?.chat_density === 'compact' ? 'small' : undefined}
                    src={iconUrl}
                />
                : null}
            <StyledSpeechBubbleContainer me={isMe} repeated={!!(isRepeated && showAvatar)}>
                {!isRepeated && <StyledName isEmbedQueueChannel={isEmbedQueueChannel} me={isMe} rights={rights}>{name}</StyledName>}
                {message.message_type === 'ninch.at/deployment' &&
                    <StyledSpeechBubble isEmbedQueueChannel={isEmbedQueueChannel} me={isMe} repeated={isRepeated} rights={rights}>
                        <strong>{`${message.title} deployed`}</strong>
                    </StyledSpeechBubble>}
                {!!message.files &&
                    <StyledFilesContainer>
                        {message.files.map((file) => (
                            <File
                                file={file}
                                key={file.file_id}
                                me={isMe}
                                repeated={isRepeated}
                                rights={rights}
                                size={message.files && message.files.length > 1 ? 'small' : 'small'}
                                time={time}
                            />
                        ))}
                    </StyledFilesContainer>}
                {!!message.repository &&
                    <StyledSpeechBubble isEmbedQueueChannel={isEmbedQueueChannel} me={isMe} repeated={isRepeated} rights={rights}>
                        <StyledGithubMessage>
                            {!!message.pull_request &&
                                <PullRequestView message={message} />}
                            {!!message.pusher &&
                                <PushView message={message} />}
                        </StyledGithubMessage>
                        <StyledTime isEmbedQueueChannel={isEmbedQueueChannel} rights={rights}>{time}</StyledTime>
                    </StyledSpeechBubble>}
                {Boolean(message.json) &&
                    <StyledSpeechBubble isEmbedQueueChannel={isEmbedQueueChannel} me={isMe} repeated={isRepeated} rights={rights}>
                        <a
                            href={getHref(message.json)}
                            rel='noopener noreferrer'
                            target='_blank'
                        >
                            JSON
                        </a>
                        <div>
                            <Highlight>{getPreview(message.json)}</Highlight>
                        </div>
                        <StyledTime isEmbedQueueChannel={isEmbedQueueChannel} rights={rights}>{time}</StyledTime>
                    </StyledSpeechBubble>}
                {(!!message.text || !!isWriting) &&
                    <StyledSpeechBubble
                        css={message._message_status === MessageStatus.REJECTED ? {borderColor: '$error'} : undefined}
                        isEmbedQueueChannel={isEmbedQueueChannel}
                        isWriting={!!isWriting}
                        me={isMe}
                        repeated={isRepeated}
                        rights={rights}
                    >
                        {!!isWriting && <Writing />}
                        {!isWriting &&
                            <>
                                <Highlight isChatMessage={true}>{String(message.text)}</Highlight>
                                <StyledTime isEmbedQueueChannel={isEmbedQueueChannel} rights={rights}>{time}</StyledTime>
                                {message._message_status === MessageStatus.PENDING &&
                                    <StyledStatus aria-live='polite' pending={true} role='status'>{t('Sending...')}</StyledStatus>}
                                {message._message_status === MessageStatus.REJECTED &&
                                    <StyledStatus failed={true} role='alert'>
                                        <InformationCircleIcon />
                                        <StyledStatusText>{t('Message was not sent')}</StyledStatusText>
                                        <StyledStatusActions>
                                            <StyledStatusButton onClick={() => onRetryMessage?.(message)} type='button'>
                                                <RefreshIcon />
                                                {t('Try again')}
                                            </StyledStatusButton>
                                            <StyledStatusButton onClick={() => onRemovePendingMessage?.(message.message_id)} type='button'>
                                                <XIcon />
                                                {t('Remove')}
                                            </StyledStatusButton>
                                        </StyledStatusActions>
                                    </StyledStatus>}
                            </>}
                    </StyledSpeechBubble>}
            </StyledSpeechBubbleContainer>
        </StyledMessageContainer>
    )
}

export default ChannelMessage
